import { uploadFiles, BaseApiRequest, getFileInfo, getFilesInfo, FileInfo } from "@robe/core-api";
import { FileValue, XCImageSize } from "@robe/core-types";
import { parseQueryParamsObject, sentryLogger } from "@robe/core-utils";
import { XcoreCore } from "XcoreCore";

type SupportedImageFormats = "webp" | "jpeg" | "png";

const SUPPORTED_IMAGE_FORMATS: SupportedImageFormats[] = ["webp", "jpeg", "png"];

/** Every change in query param generates new image in S3 so do not use different params for images if there is other way */
export type FileOptions<TSize extends XCImageSize> = Partial<TSize> & {
  fit?: "cover" | "contain" | "fill" | "inside" | "outside";
  /**
   * Optional, defaults to `false`.
   * If set to `true` max size of original image will be returned. It is recommended to use this in case of images with size around 2K.
   * If set to `false` upscaled image will be returned. This may throw `500` if original image size is too small for desired enlargement. */
  withoutEnlargement?: boolean;
  /**
   * Optional, defaults to `"webp"`.
   * `jpeg` will be converted to image with white background if original image was transparent.
   * */
  format?: SupportedImageFormats;
  /**
   * Optional, specifies the enlargement factor, defining how many times the original image should be enlarged.
   * Not present on EP
   * */
  enlargement?: number;
};

export class XcoreFiles {
  private core: XcoreCore;

  public constructor(core: XcoreCore) {
    this.core = core;
  }

  public upload(files: FileList | File[]): BaseApiRequest<FileInfo[]> {
    return uploadFiles(files, this.core.getCtx());
  }

  public getFileInfo(id: number): BaseApiRequest<FileInfo> {
    return getFileInfo(id, this.core);
  }

  public getFilesInfo(ids: number[]): BaseApiRequest<FileInfo[]> {
    return getFilesInfo(ids, this.core);
  }

  /**
   * Creates URL that file is available on. Videos use legacy BE file EP and images (including SVGs) use CDN.
   *
   * 1. Layer => Image is available in cache.
   * 2. Layer => Image is available in S3.
   * 3. Layer => Image is not available and needs to be generated by lambda. It is saved to S3 afterwards.
   *
   * Every change in any of the specified lambda params generates new image
   * so it is important to avoid excessive use of different params while dealing with the same image to save resources.
   *
   * `width` and `height` params should be used in most cases, omit them only if absolutely necessary.
   * If size parameters are not provided `/get` endpoint URL will be returned instead.
   *
   * ! For videos use `video` function instead.
   *
   * @param options.withoutEnlargement Optional, defaults to `false`.
   * If set to `true` max size of original image will be returned.
   * It is recommended to use this in case of images with size around 2K.
   * If set to `false` upscaled image will be returned.
   * This may throw `500` if original image size is too small for desired enlargement.
   *
   * @param options.format Optional, defaults ot `"webp"`.
   * `jpeg` will be converted to image with white background if original image was transparent.
   */
  public getImageUrl<TSize extends XCImageSize>(file?: FileValue<TSize>, options?: FileOptions<TSize>) {
    if (!file || !file.url) {
      return null;
    }

    if (!validateImgHostedUrl(file)) {
      return null;
    }

    // If URL returned is for `/get` EP parameters cannot be present
    if (file.url.includes("/get")) {
      return file.url;
    }

    // If size is not provided get original image stored on S3
    if (!options?.width || !options?.height) {
      const originalS3Image = transformImgHostedUrlToGetEndpointUrl(file.url);

      return originalS3Image ?? null;
    }

    const params = parseQueryParamsObject({
      width: typeof options?.width === "number" ? (options?.width * (options?.enlargement ?? 1)).toString() : "auto",
      height: typeof options?.height === "number" ? (options?.height * (options?.enlargement ?? 1)).toString() : "auto",
      fit: options?.fit ?? "cover",
      withoutEnlargement: options?.withoutEnlargement ? options?.withoutEnlargement.toString() : "false",
      ...(options?.format && SUPPORTED_IMAGE_FORMATS.includes(options.format) && { format: options.format }),
    });

    return `${file.url}${params}`;
  }

  public getHostedVideoUrl(videoId: number) {
    return `${this.core.apiUrl}/api/files/${videoId}`;
  }

  public fileSizeToString(size: number, { base = 1024, prec = 2 }: { base?: 1000 | 1024; prec?: number } = {}) {
    const units = base === 1000 ? decimalUnits : binaryUnits;

    let i = 0;
    while (size > base) {
      if (i > 3) break;
      size = size / base;
      i++;
    }

    return `${size.toFixed(prec)} ${units[i]}`;
  }
}

const decimalUnits = ["B", "kB", "MB", "GB", "TB"];
const binaryUnits = ["B", "KiB", "MiB", "GiB", "TiB"];

const validateImgHostedUrl = (file: FileValue) => {
  try {
    const parsedUrl = new URL(file.url!);
    const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);

    // Expect path like /v1/image/get/:hash or /v1/image/resize/:hash
    const action = pathSegments[3]; // should be 'get' or 'resize'
    const hash = pathSegments[4]; // the actual hash part

    if ((action === "get" || action === "resize") && (!hash || hash.length === 0)) {
      sentryLogger({
        error: new Error(`Missing hash in CDN URL: ${file.url!}`),
        context: {
          fileId: file.id,
        },
      });

      return false;
    }

    return true;
  } catch (error) {
    sentryLogger({
      error,
      message: `Invalid URL format: ${file.url!}`,
      context: {
        fileId: file.id,
      },
    });

    return false;
  }
};

const transformImgHostedUrlToGetEndpointUrl = (url: string) => {
  try {
    const urlObj = new URL(url);

    urlObj.pathname = urlObj.pathname.replace("/resize/", "/get/");

    // Remove all query params
    urlObj.search = "";

    return urlObj.toString();
  } catch (error) {
    return undefined;
  }
};
